/* El archivo completo de documentos de un proceso (pantalla 7c).
 *
 * DESTINO DE EXCEPCIÓN, y esa es toda su tesis. El documento 7 corrige una propuesta anterior que
 * convertía los documentos en una segunda ficha con su propio veredicto: lo que hace falta a diario
 * vive en la sección 06 de la ficha —los diez que sirven para decidir, agrupados y con el trámite
 * colapsado— y esta pantalla es a donde se viene cuando eso no basta: buscar un acta, un CDP, o uno
 * de los que quedaron «sin clasificar» porque su nombre no dice nada.
 *
 * Por eso aquí SÍ está todo —los veintidós, no los diez— y por eso hay buscador y orden, que en la
 * ficha serían ruido.
 *
 * ⚠ Lo que NO tiene, a diferencia del documento: las casillas para elegir qué lee el análisis. Ese
 * cupo tiene un dueño, la hoja de confirmación (7b), y ponerlo también aquí sería mantener el mismo
 * estado en dos pantallas — el gemelo que este proyecto ya retiró del puntaje. Desde aquí se va a
 * la hoja, que es donde se elige.
 */
import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { obtener } from '../api/cliente'
import { useEffect } from 'react'
import { ErrorApi } from '../api/cliente'
import { entero, fecha, normalizarNombre, normalizarObjeto, plazo, urlSegura } from '../api/formato'
import { BLOQUES, bloqueDe } from '../api/documentos'
import type { Documento, RespuestaFicha } from '../api/tipos'
import { ETIQUETA_BANDA } from '../api/formato'
import { IconoExterno, IconoVolver } from '../ui/Iconos'
import './Documentos.css'

type Orden = 'lectura' | 'fecha' | 'nombre'

const ORDENES: { clave: Orden; etiqueta: string }[] = [
  { clave: 'lectura', etiqueta: 'Orden: qué leer primero' },
  { clave: 'fecha', etiqueta: 'Orden: cargado más reciente' },
  { clave: 'nombre', etiqueta: 'Orden: nombre' },
]

/* Los mismos bloques que la sección 06 de la ficha, MÁS el pie que allá va colapsado: aquí se
   viene precisamente a buscar un acta o algo sin clasificar, así que lo que allá es ruido aquí es
   el motivo de la visita. La definición es UNA (`api/documentos.ts`) — dos agrupaciones distintas
   para los mismos documentos serían dos respuestas a la misma pregunta según por dónde se llegue. */
const AL_PIE = { titulo: 'De trámite y sin clasificar',
                 nota: 'CDP, actas, certificaciones y nombres opacos — opaco no es irrelevante' }

export function Documentos() {
  const { id } = useParams()
  const [datos, setDatos] = useState<RespuestaFicha | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [texto, setTexto] = useState('')
  const [orden, setOrden] = useState<Orden>('lectura')
  const [abierto, setAbierto] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const c = new AbortController()
    obtener<RespuestaFicha>(`/api/oportunidades/${id}`, c.signal)
      .then(setDatos)
      .catch((e: unknown) => {
        if (!c.signal.aborted) setError(e instanceof ErrorApi ? e.message : String(e))
      })
    return () => c.abort()
  }, [id])

  const proceso = datos?.procesos?.[0]
  const entidad = proceso ? datos?.entidades?.[proceso.entidad_nit] : undefined
  const docs = useMemo(() => proceso?.documentos ?? [], [proceso])

  const bloques = useMemo(() => {
    const q = texto.trim().toLowerCase()
    /* El buscador mira el NOMBRE y la CATEGORÍA. Es lo único que hay: el contenido del archivo no
       está indexado, y dejar que parezca que sí —«no encontré "experiencia"»— haría creer que el
       documento no lo dice cuando lo que pasa es que nadie lo ha leído. */
    const visibles = q
      ? docs.filter((d) => `${d.nombre} ${d.categoria}`.toLowerCase().includes(q))
      : docs
    const ordenados = [...visibles]
    if (orden === 'fecha') ordenados.sort((a, b) => String(b.fecha ?? '').localeCompare(String(a.fecha ?? '')))
    if (orden === 'nombre') ordenados.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
    // `lectura` es el orden en que vienen: el clasificador ya los ordenó por prioridad.
    const porBloque = BLOQUES.map((b, i) => ({
      titulo: b.titulo, nota: b.nota,
      docs: ordenados.filter((d) => bloqueDe(d) === i),
    }))
    return [...porBloque, { ...AL_PIE, docs: ordenados.filter((d) => bloqueDe(d) < 0) }]
      .filter((g) => g.docs.length)
  }, [docs, texto, orden])

  const sel = docs.find((d) => d.nombre === abierto) ?? null

  if (error) {
    return (
      <div className="andamio">
        <h1>No pude abrir los documentos</h1>
        <p>{error}</p>
        <p><Link to={`/proceso/${id}`}>Volver a la ficha</Link></p>
      </div>
    )
  }
  if (!datos || !proceso) return <p className="rotulo">Cargando</p>

  const v = datos.veredicto
  const encontrados = bloques.reduce((n, g) => n + g.docs.length, 0)

  return (
    <div className="archivo">
      <div className="archivo-barra">
        <Link className="accion" to={`/proceso/${id}`}>
          <IconoVolver /> Volver a la ficha
        </Link>
        <span className="archivo-proceso" title={proceso.descripcion || proceso.objeto}>
          {normalizarObjeto(proceso.descripcion || proceso.objeto)}
        </span>
        <span className="archivo-estado">
          {v?.banda && v.afinidad !== null && (
            <span className={`archivo-banda b-${v.banda}`}>{ETIQUETA_BANDA[v.banda]}</span>
          )}
          {[v?.afinidad != null ? `afinidad ${v.afinidad}` : null,
            plazo(proceso.fecha_cierre).toLowerCase()].filter(Boolean).join(' · ')}
        </span>
      </div>

      <header className="archivo-cabeza">
        <div>
          <h1>Documentos del proceso · {entero(docs.length)}</h1>
          <p>
            En vivo desde SECOP II. La categoría es una <b>heurística por el nombre del archivo</b>,
            no el tipo que asignó la entidad: sirve para ordenar qué leer primero, no para afirmar
            qué es cada documento.
          </p>
        </div>
        <div className="archivo-controles">
          <input type="search" className="buscar-docs" placeholder="Buscar en los documentos…"
                 value={texto} onChange={(e) => setTexto(e.target.value)} />
          <select value={orden} onChange={(e) => setOrden(e.target.value as Orden)}
                  aria-label="Orden">
            {ORDENES.map((o) => <option key={o.clave} value={o.clave}>{o.etiqueta}</option>)}
          </select>
        </div>
      </header>

      {/* La procedencia, en su franja. Es una pantalla de archivo: quien viene aquí a no encontrar
          algo necesita saber si es que no existe o si es que no lo cubrimos. */}
      <p className="archivo-fuente">
        <span>
          Cobertura del dato: <b>publicados desde 2025</b>; si el proceso subió documentos antes, no
          aparecen aquí.
        </span>
        {urlSegura(proceso.url) && (
          <a href={urlSegura(proceso.url) as string} target="_blank" rel="noreferrer">
            Ficha oficial en SECOP <IconoExterno />
          </a>
        )}
      </p>

      <div className="archivo-cuerpo">
        <section className="archivo-lista" aria-label="Documentos">
          <div className="archivo-encabezado" role="presentation">
            <span>Documento</span>
            <span>Categoría</span>
            <span className="der">Cargado</span>
          </div>

          {encontrados === 0 ? (
            <p className="vacio-docs">
              {texto
                ? <>Ningún documento se llama así. El buscador mira el <b>nombre y la categoría</b>,
                    no lo que dice adentro: para eso está «Analizar el pliego».</>
                : 'Este proceso no tiene documentos publicados todavía.'}
            </p>
          ) : bloques.map((g) => (
            <div key={g.titulo}>
              <div className="archivo-grupo">
                <span className="archivo-grupo-titulo">{g.titulo} · {entero(g.docs.length)}</span>
                <span className="archivo-grupo-nota">{g.nota}</span>
              </div>
              <ul className="archivo-filas">
                {g.docs.map((d) => (
                  <li key={d.nombre}>
                    <button type="button"
                            className={`archivo-fila${d.nombre === abierto ? ' activa' : ''}`}
                            aria-current={d.nombre === abierto}
                            onClick={() => setAbierto(d.nombre)}>
                      <span className="archivo-nombre">{d.nombre}</span>
                      <span className="archivo-cat">{d.categoria}</span>
                      <span className="archivo-fecha der cifra">{fecha(d.fecha, '—')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <aside className="archivo-panel" aria-live="polite">
          {sel ? <Detalle d={sel} procesoId={id ?? ''} entidad={entidad?.nombre} /> : (
            <p className="vacio-docs">
              Toca un documento para ver de qué tipo es y abrirlo en SECOP.
            </p>
          )}
        </aside>
      </div>
    </div>
  )
}

/**
 * El panel del documento abierto.
 *
 * ⚠ Dice lo que se SABE del archivo —su nombre, su categoría heurística, cuándo se cargó— y no
 * finge saber lo que dice adentro. El 7c del diseño pinta aquí «Lo que hay dentro» con una cita
 * verbatim; esos datos existen en el grafo (`bulk_pliegos` extrae 24 columnas con cita verificada)
 * pero no llegan al snapshot del panel — es la fase 8b, pendiente. Poner un bloque que siempre diga
 * «nadie ha leído esto» sería peor que no ponerlo, así que en su lugar va lo único que hoy puede
 * cumplir la promesa: el botón que manda a leerlo.
 */
function Detalle({ d, procesoId, entidad }: {
  d: Documento
  procesoId: string
  entidad?: string | undefined
}) {
  const href = urlSegura(d.url)
  return (
    <div className="doc-detalle">
      <span className="rotulo">{d.categoria}</span>
      <h2>{d.nombre}</h2>
      <p className="doc-detalle-meta">
        {[entidad ? `Publicado por ${normalizarNombre(entidad)}` : null,
          d.fecha ? `cargado el ${fecha(d.fecha)}` : null].filter(Boolean).join(' · ')}
      </p>
      <p className="nota-archivo">
        La categoría sale del <b>nombre del archivo</b>, no del tipo que declaró la entidad. Si el
        nombre no dice nada, la categoría tampoco — ábrelo para saber qué es.
      </p>
      <div className="doc-detalle-acciones">
        {href ? (
          <a className="boton-primario" href={href} target="_blank" rel="noreferrer">
            Abrir el archivo <IconoExterno />
          </a>
        ) : (
          <p className="nota-archivo">SECOP no publicó el enlace de descarga de este archivo.</p>
        )}
        <Link className="accion" to={`/proceso/${procesoId}/pliego`}>
          Analizar el pliego
        </Link>
      </div>
      <p className="descargo">
        Nadie ha leído todavía lo que dice adentro. «Analizar el pliego» lee los principales y trae
        los requisitos con su cita; ahí se elige cuáles.
      </p>
    </div>
  )
}
